import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";

import style from "./style.module.scss";
import WizardFooter from "../wizard-footer";
import PageContent from "../page-content";
import PriceTable from "../price-table";
import PromoCodeForm from "../promo-code-form";
import Form from "./form";
import { getProductType } from "../../../product";
import { getShippingPrice } from "./wilayas";
import { requireLogin } from "../../../security";
import { productQueryOptions, useSaveProductMutation } from "../../../data/queries/products";
import { useAuth } from "../../../data/context/auth-context";
import { sendOrderEmailForProduct } from "../../../api/order-email";
import { Button, Spinner } from "../../../ui";
import { trackPurchase } from "../../../analytics";
import type { ShippingInfo } from "../../../product/types";

function Shipping() {
  const { uid } = useParams();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { data: product, isLoading } = useQuery(productQueryOptions(uid || ""));
  const saveProductMutation = useSaveProductMutation();
  const [loading, setLoading] = useState(false);
  const [wilaya, setWilaya] = useState("16");
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const confirm = (shipping: ShippingInfo) => {
    if (!product || !user || !profile) return;

    setLoading(true);
    saveProductMutation.mutate(
      {
        product: {
          ...product,
          shipping,
        },
        serverChanges: { status: "order" },
        userId: profile.uid,
      },
      {
        onSuccess: () => {
          // Track purchase
          const productType = getProductType(product.name);
          const shippingPrice = getShippingPrice(shipping.wilaya);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const totalPrice =
            productType?.price(product.options as any, product.promoCode, shippingPrice) ?? 0;
          trackPurchase(product.name, totalPrice);

          // Send order confirmation emails (async, no need to wait)
          sendOrderEmailForProduct(product).catch((error) => {
            console.error("Failed to send order email:", error);
            // Continue anyway - order is already saved
          });

          navigate(`/confirmation/${product.uid}`);
        },
        onError: () => {
          if (mountedRef.current) {
            setLoading(false);
          }
        },
      },
    );
  };

  const applyCode = (code: string) => {
    if (!product || !user || !profile) return;

    setLoading(true);
    saveProductMutation.mutate(
      {
        product: {
          ...product,
          promoCode: code,
        },
        userId: profile.uid,
      },
      {
        onSettled: () => {
          if (mountedRef.current) {
            setLoading(false);
          }
        },
      },
    );
  };

  const onChangeWilaya = (newWilaya: string) => {
    if (newWilaya !== wilaya) {
      setWilaya(newWilaya);
    }
  };

  if (isLoading || !product) {
    return <Spinner />;
  }

  // Check if product status is not draft, redirect or show error
  if (product.status !== "draft") {
    return <Spinner />;
  }

  const productType = getProductType(product.name);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isReady = productType?.isReady(product.options as any) ?? false;

  return (
    <>
      <PageContent
        title="Livraison et paiement"
        description="Renseignez vos informations de livraison pour finaliser votre commande."
        className={style.content}
      >
        <div className={style.columns}>
          <div className={style.main}>
            <Form
              formId="shipping-form"
              product={product}
              user={{
                name: profile?.name || user?.displayName || "",
                profile: {
                  email: profile?.email || user?.email || "",
                },
              }}
              onSubmit={confirm}
              onChangeWilaya={onChangeWilaya}
            />
          </div>

          <div className={style.priceTable}>
            <PriceTable
              product={{
                ...product,
                shipping: {
                  ...product.shipping,
                  wilaya,
                },
              }}
            />
            <PromoCodeForm product={product} onApply={applyCode} />
          </div>
        </div>
      </PageContent>
      <WizardFooter uid={product.uid} step={4} enabledSteps={isReady ? [1, 2, 3, 4] : [1, 2, 3]}>
        <Button type="submit" form="shipping-form" isDisabled={loading} isPrimary>
          {loading && <Spinner />}
          Valider votre commande
        </Button>
      </WizardFooter>
    </>
  );
}

export default requireLogin()(Shipping);
